import { useState, useEffect, useCallback } from 'react';
import {
  X, Loader2, FileCode, FileText, Check, ChevronRight, Clock,
  AlertCircle, ShieldCheck,
} from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { apiFetch } from '../lib/supabase';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Sprint 3 — HITL Side-by-Side Evidence Drawer.
 *
 * Slides in from the right when a reviewer expands a HITL item. Shows:
 *   • LEFT card:  IFC element value (deterministic engine reading).
 *   • RIGHT card: PDF / BEP source value (what the BEP says it should be).
 *   • Carolina-blue radio: reviewer selects which is the source of truth.
 *   • Decision log: append-only history, oldest-first, shown at the bottom.
 *
 * The Golden Thread provenance — source_document + source_page — is shown
 * next to every BEP citation so the reviewer can flip to the PDF and verify.
 *
 * Props:
 *   itemId           — aim_register row id to load evidence for.
 *   onClose          — called when the drawer is dismissed.
 *   onReviewApplied  — called after a successful review POST so the parent
 *                      can refresh its queue + remove the item from the list.
 */
export function HITLEvidenceDrawer({ itemId, onClose, onReviewApplied }) {
  const [loading, setLoading] = useState(true);
  const [evidence, setEvidence] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Source-of-truth selection — "ifc" | "pdf" | "". Sets the Carolina-blue
  // ring on whichever card the reviewer picked.
  const [sourceOfTruth, setSourceOfTruth] = useState('');
  const [evidenceNotes, setEvidenceNotes] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');

  const fetchEvidence = useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    setError('');
    try {
      const resp = await apiFetch(`${API_URL}/api/audits/hitl/${itemId}/evidence`);
      if (!resp.ok) {
        const t = await resp.text().catch(() => '');
        throw new Error(`Could not load evidence (${resp.status}): ${t.slice(0, 200)}`);
      }
      const data = await resp.json();
      setEvidence(data);
    } catch (e) {
      setError(e.message || 'Failed to load evidence');
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  useEffect(() => { fetchEvidence(); }, [fetchEvidence]);

  const submitReview = async (action) => {
    if (!itemId) return;
    setSubmitting(true);
    setError('');
    try {
      const body = {
        action,
        notes: reviewNotes,
        source_of_truth: sourceOfTruth,
        evidence_notes: evidenceNotes,
      };
      const resp = await apiFetch(`${API_URL}/api/audits/hitl/${itemId}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const t = await resp.text().catch(() => '');
        throw new Error(`Review failed (${resp.status}): ${t.slice(0, 200)}`);
      }
      const result = await resp.json();
      onReviewApplied?.(itemId, action, result.log_length);
    } catch (e) {
      setError(e.message || 'Review failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!itemId) return null;

  return (
    <>
      {/* Scrim — clicking it dismisses the drawer */}
      <div
        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]"
        onClick={onClose}
        data-testid="evidence-drawer-scrim"
      />

      {/* Drawer panel */}
      <aside
        className="fixed right-0 top-0 z-50 h-screen w-full max-w-3xl bg-card shadow-2xl overflow-y-auto border-l border-border"
        data-testid="evidence-drawer"
      >
        {/* Header strip — solid Pantone 533 navy, matches the sidebar refresh */}
        <header className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-3 bg-[hsl(var(--primary))] text-white border-b border-white/10">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-white/70">
              HITL Evidence
            </p>
            <h2 className="text-base font-semibold truncate">
              {evidence?.ifc?.name || 'Loading…'}
            </h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/10"
            onClick={onClose}
            data-testid="evidence-drawer-close-btn"
          >
            <X className="h-4 w-4" />
          </Button>
        </header>

        <div className="p-5 space-y-5">
          {error && (
            <div className="rounded-md border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/5 px-3 py-2 text-sm text-[hsl(var(--destructive))]">
              {error}
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              Loading evidence…
            </div>
          )}

          {!loading && evidence && (
            <>
              {/* IFC element header */}
              <div className="rounded-md border border-border bg-muted/40 p-3">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <Badge variant="outline">{evidence.ifc.ifc_class || 'Unknown'}</Badge>
                  {evidence.ifc.object_type && (
                    <Badge variant="outline" className="text-[10px]">
                      {evidence.ifc.object_type}
                    </Badge>
                  )}
                  <span className="text-xs text-muted-foreground">
                    GlobalID <code>{evidence.ifc.global_id}</code>
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  Storey: <strong>{evidence.ifc.storey || '—'}</strong>
                  {' · '}Space: <strong>{evidence.ifc.space || '—'}</strong>
                  {evidence.item.confidence_score != null && (
                    <>
                      {' · '}Confidence:{' '}
                      <strong>{Math.round(evidence.item.confidence_score || 0)}%</strong>
                    </>
                  )}
                </div>
              </div>

              {/* SIDE-BY-SIDE EVIDENCE — one row per failing rule */}
              <section data-testid="evidence-rows">
                <h3 className="text-sm font-semibold text-foreground mb-2">
                  Per-rule evidence
                  <span className="ml-2 text-xs text-muted-foreground">
                    ({evidence.pdf_sources.length} flagged check
                    {evidence.pdf_sources.length !== 1 ? 's' : ''})
                  </span>
                </h3>
                <div className="space-y-3">
                  {evidence.pdf_sources.length === 0 && (
                    <p className="text-sm text-muted-foreground italic">
                      No failing checks for this element — it may already have been resolved.
                    </p>
                  )}
                  {evidence.pdf_sources.map((src, idx) => (
                    <EvidenceRow key={`${src.rule_id}-${idx}`} row={src} />
                  ))}
                </div>
              </section>

              {/* SOURCE-OF-TRUTH SELECTION — Carolina blue active states */}
              <section data-testid="evidence-sot-selection">
                <h3 className="text-sm font-semibold text-foreground mb-2">
                  Select source of truth
                  <span className="ml-2 text-xs text-muted-foreground font-normal">
                    Required when the IFC and BEP disagree.
                  </span>
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <SourceOfTruthCard
                    selected={sourceOfTruth === 'ifc'}
                    onSelect={() => setSourceOfTruth(sourceOfTruth === 'ifc' ? '' : 'ifc')}
                    label="IFC element is authoritative"
                    Icon={FileCode}
                    description="Trust the IFC file. Update the BEP / O&M record to match."
                    testid="sot-ifc"
                  />
                  <SourceOfTruthCard
                    selected={sourceOfTruth === 'pdf'}
                    onSelect={() => setSourceOfTruth(sourceOfTruth === 'pdf' ? '' : 'pdf')}
                    label="PDF / BEP is authoritative"
                    Icon={FileText}
                    description="Trust the BEP. Fix the IFC element in the authoring tool."
                    testid="sot-pdf"
                  />
                </div>
                <textarea
                  className="mt-3 w-full rounded-md border border-input bg-card px-3 py-2 text-sm placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent))]/40"
                  rows={2}
                  placeholder="Why is this the source of truth? (optional, max 1000 chars)"
                  value={evidenceNotes}
                  maxLength={1000}
                  onChange={(e) => setEvidenceNotes(e.target.value)}
                  data-testid="evidence-notes"
                />
              </section>

              {/* DECISION LOG — append-only history */}
              {evidence.decision_log.length > 0 && (
                <DecisionLog log={evidence.decision_log} />
              )}

              {/* REVIEW ACTIONS */}
              <section
                className="sticky bottom-0 -mx-5 -mb-5 mt-4 border-t border-border bg-card px-5 py-3"
                data-testid="evidence-drawer-actions"
              >
                <input
                  type="text"
                  className="mb-2 w-full rounded-md border border-input bg-card px-3 py-2 text-sm placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent))]/40"
                  placeholder="Optional review notes…"
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  data-testid="evidence-review-notes"
                />
                <div className="flex flex-wrap items-center gap-2 justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => submitReview('approve')}
                    disabled={submitting}
                    data-testid="evidence-approve-btn"
                    className="text-[hsl(var(--success))] border-[hsl(var(--success))]/40 hover:bg-[hsl(var(--success))]/10"
                  >
                    {submitting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1" />}
                    Approve
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => submitReview('approve_with_notes')}
                    disabled={submitting || !reviewNotes.trim()}
                    title={!reviewNotes.trim() ? 'Add review notes to enable' : ''}
                    data-testid="evidence-approve-with-notes-btn"
                  >
                    Approve with notes
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => submitReview('reject')}
                    disabled={submitting}
                    data-testid="evidence-reject-btn"
                    className="text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40 hover:bg-[hsl(var(--destructive))]/10"
                  >
                    <X className="h-3.5 w-3.5 mr-1" />
                    Reject
                  </Button>
                </div>
              </section>
            </>
          )}
        </div>
      </aside>
    </>
  );
}

/* ── Internal subcomponents ───────────────────────────────────────── */

function SourceOfTruthCard({ selected, onSelect, label, Icon, description, testid }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={testid}
      aria-pressed={selected}
      className={`relative text-left rounded-md border-2 p-3 transition-all
        ${selected
          ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 ring-2 ring-[hsl(var(--accent))]/30 shadow-sm'
          : 'border-border bg-card hover:border-[hsl(var(--accent))]/50'
        }`}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`h-4 w-4 ${selected ? 'text-[hsl(var(--accent-foreground))]' : 'text-muted-foreground'}`} />
        <span className={`text-sm font-semibold ${selected ? 'text-[hsl(var(--accent-foreground))]' : 'text-foreground'}`}>
          {label}
        </span>
        {selected && (
          <Check className="h-3.5 w-3.5 ml-auto text-[hsl(var(--accent-foreground))]" />
        )}
      </div>
      <p className={`text-xs ${selected ? 'text-[hsl(var(--accent-foreground))]/80' : 'text-muted-foreground'}`}>
        {description}
      </p>
    </button>
  );
}

function EvidenceRow({ row }) {
  const sevColor = {
    critical: 'text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40',
    major: 'text-[hsl(var(--warning))] border-[hsl(var(--warning))]/50',
    minor: 'text-muted-foreground border-border',
  }[row.severity?.toLowerCase()] || 'text-muted-foreground border-border';

  return (
    <div className="rounded-md border border-border bg-card p-3 shadow-sm">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Badge variant="outline" className={`uppercase ${sevColor}`}>
          {row.severity || 'major'}
        </Badge>
        <span className="text-xs font-semibold text-foreground">{row.rule_id}</span>
        <Badge variant="outline" className="text-[10px]">{row.category}</Badge>
        <Badge variant="outline" className={`text-[10px] ${sevColor}`}>
          {row.status}
        </Badge>
        {row.source_document && (
          <span className="ml-auto text-[11px] text-muted-foreground inline-flex items-center gap-1">
            <FileText className="h-3 w-3" />
            {row.source_document}
            {row.source_page > 0 && ` · p.${row.source_page}`}
          </span>
        )}
      </div>
      {row.rule_name && row.rule_name !== row.rule_id && (
        <p className="text-xs text-foreground mb-2">{row.rule_name}</p>
      )}

      <div className="grid grid-cols-2 gap-2 mt-2">
        <EvidenceCell
          label="IFC value"
          Icon={FileCode}
          value={row.value_found_in_ifc || '—'}
          field={row.field_to_check}
          accent="ifc"
        />
        <EvidenceCell
          label="BEP requires"
          Icon={FileText}
          value={row.expected_pattern || '(rule has no expected value)'}
          field={row.field_to_check}
          accent="pdf"
        />
      </div>

      {row.issue && (
        <p className="mt-2 text-[11px] text-[hsl(var(--destructive))]/90">
          <AlertCircle className="h-3 w-3 inline mr-1" />
          {row.issue}
        </p>
      )}
    </div>
  );
}

function EvidenceCell({ label, Icon, value, field, accent }) {
  // ``accent`` distinguishes the IFC column from the PDF column. We use a
  // very subtle muted-blue background tint so the two columns read as
  // different sources without competing with the Carolina-blue selection
  // state of the source-of-truth picker below.
  const tint = accent === 'ifc'
    ? 'bg-[hsl(var(--info))]/5'
    : 'bg-[hsl(var(--accent))]/5';
  return (
    <div className={`rounded-md border border-border p-2 ${tint}`}>
      <div className="flex items-center gap-1 mb-1">
        <Icon className="h-3 w-3 text-muted-foreground" />
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
      </div>
      {field && (
        <p className="text-[10px] text-muted-foreground mb-0.5">{field}</p>
      )}
      <p className="text-xs text-foreground break-words font-mono">{value}</p>
    </div>
  );
}

function DecisionLog({ log }) {
  return (
    <section data-testid="evidence-decision-log">
      <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-[hsl(var(--success))]" />
        Decision log
        <span className="text-xs text-muted-foreground font-normal">
          Append-only — {log.length} {log.length === 1 ? 'entry' : 'entries'}
        </span>
      </h3>
      <div className="rounded-md border border-border bg-muted/30">
        {log.map((entry, idx) => (
          <div
            key={`${entry.reviewed_at}-${idx}`}
            className="px-3 py-2 border-b border-border last:border-b-0 text-xs"
          >
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <Badge
                variant="outline"
                className={
                  entry.action?.startsWith('approve')
                    ? 'text-[hsl(var(--success))] border-[hsl(var(--success))]/40'
                    : 'text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40'
                }
              >
                {entry.action}
              </Badge>
              <span className="text-foreground font-medium">{entry.reviewed_by}</span>
              <span className="text-muted-foreground inline-flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {entry.reviewed_at ? new Date(entry.reviewed_at).toLocaleString() : '—'}
              </span>
              {entry.source_of_truth && (
                <Badge
                  variant="outline"
                  className="text-[10px] text-[hsl(var(--accent-foreground))] bg-[hsl(var(--accent))]/30 border-[hsl(var(--accent))]"
                >
                  SoT: {entry.source_of_truth.toUpperCase()}
                </Badge>
              )}
            </div>
            {entry.notes && (
              <p className="text-muted-foreground italic">"{entry.notes}"</p>
            )}
            {entry.evidence_notes && (
              <p className="text-muted-foreground mt-1">
                <ChevronRight className="h-3 w-3 inline" /> {entry.evidence_notes}
              </p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
